/**
 * Who Foundation legally is, in one place.
 *
 * This used to be stated twice — in the homepage's COMPANY block and again,
 * verbatim, inside the privacy policy's "Who we are" paragraph — and both
 * named a different company: a live, trading estate agency and
 * Foundation's own customer. An agency doing basic due diligence before
 * handing over its vendor list, buyer database and fee structure would find
 * that the entity processing all of it was a competing agent. It is now one
 * object, so the entity can never again be right in one place and wrong in
 * another.
 *
 * A UK limited company must show its registered name, number and office on
 * its website (Companies Act 2006 s.82 / The Company, Limited Liability
 * Partnership and Business (Names and Trading Disclosures) Regulations 2015,
 * and the E-Commerce Regulations 2002), so these aren't only a trust nicety.
 *
 * Verified against the Companies House register on 7 September 2026:
 * SOFTWARE BUILDERS LTD, company 17431696, incorporated 1 September 2026.
 * The entity is NOT "Foundation Software Ltd" — that was a plausible guess
 * that sat here for weeks. Foundation is a trading name and brand of it.
 *
 * Another company, an estate agency that is ours only as a customer, shares
 * the registered office. Its name and number must never be carried into
 * these fields; that is this file's original bug, one step on.
 *
 * Every consumer below still renders a field only when it is filled, so a
 * future blank disappears rather than printing a placeholder.
 */
export const FOUNDATION_COMPANY = {
  // Trading name shown to customers.
  name: "Foundation",
  // The company that actually contracts with, and processes data for, tenants.
  legalName: "Software Builders Ltd",
  // How the relationship is worded wherever the entity is disclosed.
  tradingNameNote: "Foundation is a trading name of",
  jurisdiction: "England & Wales",
  companyNumber: "17431696",
  registeredAddress: "66 Chertsey Road, Windlesham, England, GU20 6HP",
  // Contact. These are the only routes a prospect has to reach Foundation
  // before they're a customer, so they belong in the footer, not buried in
  // the body of a legal page.
  email: "hello@foundationagent.co.uk",
  supportEmail: "hello@foundationagent.co.uk",
  privacyEmail: "privacy@foundationagent.co.uk",
  phone: "",
  site: "foundationagent.co.uk",
};

/**
 * "Foundation Software Ltd, registered in England & Wales, under company
 * number 1234567, with its registered office at …" — a clause the caller
 * frames ("Foundation is a trading name of …"), with each part dropping out
 * cleanly if we don't have it yet. Shared by the privacy policy and the
 * terms so the wording can't drift between them.
 */
export function companyDisclosureClause(c = FOUNDATION_COMPANY) {
  const parts = [`${c.legalName}, registered in ${c.jurisdiction}`];
  if (c.companyNumber) parts.push(`under company number ${c.companyNumber}`);
  if (c.registeredAddress) parts.push(`with its registered office at ${c.registeredAddress}`);
  return parts.join(", ");
}

/** The same thing as discrete lines, for the footer and the About block.
 *  Leads with the trading-name framing so the entity doesn't read as a bare
 *  name dropped on the page. */
export function companyDisclosureLines(c = FOUNDATION_COMPANY) {
  return [
    c.legalName ? `${c.tradingNameNote} ${c.legalName}` : "",
    c.companyNumber ? `Registered in ${c.jurisdiction}, company number ${c.companyNumber}` : "",
    c.registeredAddress,
  ].filter(Boolean);
}
